import { useMutation, useQueryClient } from '@tanstack/react-query';
import { endpoints, ID } from '@rossum/api-client';
import { QueueRule } from '@rossum/api-client/schemas';
import { useApiClient } from '../../../lib/apiClient';
import { RULES_QUERY_KEY } from './rulesQueryKeys';

export const usePatchRule = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    onMutate: async ({ patchedRuleId, patchedRule }) => {
      await queryClient.cancelQueries([RULES_QUERY_KEY]);

      const previousQueries = queryClient.getQueriesData<QueueRule[]>([
        RULES_QUERY_KEY,
        'list',
      ]);

      queryClient.setQueriesData<QueueRule[]>(
        [RULES_QUERY_KEY, 'list'],
        oldRules =>
          oldRules?.map(rule =>
            rule.id === patchedRuleId ? { ...rule, ...patchedRule } : rule
          )
      );

      return { previousQueries };
    },
    onError: (_error, _variables, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (_data, _error, variables) => {
      if (!variables.skipInvalidation) {
        queryClient.invalidateQueries([RULES_QUERY_KEY]);
      }
    },
    mutationFn: ({
      patchedRuleId,
      patchedRule,
    }: {
      patchedRuleId: ID;
      patchedRule: Partial<QueueRule>;
      skipInvalidation?: boolean;
    }) => api.request(endpoints.rules.patch(patchedRuleId, patchedRule)),
  });
};
